import {
    Alert,
    Badge,
    Box,
    Button,
    Checkbox,
    Collapse,
    Grid,
    Group,
    Modal,
    NumberInput,
    Paper,
    ScrollArea,
    SegmentedControl,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityDistrictRO } from '@routellers/types/dist/city-district/city-district.ro';
import { CityFoodRO } from '@routellers/types/dist/city-food/city-food.ro';
import { CityGuideRefreshRO } from '@routellers/types/dist/city-guide/city-guide-refresh.ro';
import { GuideLocaleStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-locale-status.enum';
import { GuideStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { DistrictSelect } from './district-select';
import { LanguageChips, type LanguageChip } from './editor';

type TextDraft = {
    name: string;
    portrait: string;
    why: string;
    stay: string;
    status: GuideLocaleStatusEnum;
};

type CardDraft = {
    title: string;
    lat: string;
    lng: string;
    status: GuideStatusEnum;
};

type FoodCard = {
    title: string;
    districtId: number | null;
    status: GuideStatusEnum;
};

type FoodText = {
    name: string;
    pitch: string;
    status: GuideLocaleStatusEnum;
};

type PlaceDraft = {
    establishmentId: number;
    title: string;
    orderNotes: Record<string, string>;
};

const textReady = (draft: { name: string; portrait: string; why: string; stay: string }) =>
    !!draft.name.trim() && !!(draft.portrait.trim() || draft.why.trim() || draft.stay.trim());

const localeOf = <T extends { language: Languages }>(rows: T[] | undefined, language: Languages) =>
    rows?.find((row) => row.language === language);

export const CityGuidePanel = ({
    cityId,
    country,
}: {
    cityId: number;
    country: CountriesEnum | string;
}) => {
    const { t } = useTranslation();
    const languages = useMemo(() => contentLanguagesForCountry(country), [country]);
    const [lang, setLang] = useState<Languages>(languages[0] || Languages.RU);
    const [section, setSection] = useState('districts');
    const [token, setToken] = useState(0);
    const [catalogVersion, setCatalogVersion] = useState(0);
    const [districtDirty, setDistrictDirty] = useState(false);
    const [foodDirty, setFoodDirty] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [force, setForce] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [report, setReport] = useState<CityGuideRefreshRO | null>(null);
    const dirty = districtDirty || foodDirty;

    useEffect(() => {
        if (!languages.includes(lang)) setLang(languages[0] || Languages.RU);
    }, [languages, lang]);

    const refresh = async () => {
        setConfirm(false);
        setRefreshing(true);
        setReport(null);

        try {
            const { data } = await api.cities.refreshGuide({ id: cityId, data: { force } });

            setReport(data || null);
            setToken((value) => value + 1);
            setCatalogVersion((value) => value + 1);
            setForce(false);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Stack gap='md'>
            <Paper withBorder p='md' radius='md'>
                <Group justify='space-between' align='flex-start' wrap='wrap' gap='md'>
                    <Text size='sm' c='dimmed' maw={560}>
                        {t('pages.cityDetail.guide.lede')}
                    </Text>
                    <Button
                        leftSection={<IconRefresh size={16} />}
                        loading={refreshing}
                        onClick={() => setConfirm(true)}
                    >
                        {t('pages.cityDetail.guide.refresh')}
                    </Button>
                </Group>
                {refreshing ? (
                    <Text size='sm' mt='sm'>
                        {t('pages.cityDetail.guide.running')}
                    </Text>
                ) : null}
                {report ? (
                    <Alert
                        mt='md'
                        color='green'
                        variant='light'
                        title={t('pages.cityDetail.guide.reportTitle')}
                        withCloseButton
                        onClose={() => setReport(null)}
                    >
                        <Stack gap={4}>
                            <Text size='sm'>
                                {t('pages.cityDetail.guide.reportDistricts', {
                                    created: report.districtsCreated,
                                    filled: report.districtsFilled,
                                })}
                            </Text>
                            <Text size='sm'>
                                {t('pages.cityDetail.guide.reportPlaces', {
                                    updated: report.attractionsUpdated,
                                    remaining: report.attractionsRemaining,
                                })}
                            </Text>
                            <Text size='sm'>
                                {t('pages.cityDetail.guide.reportTables', {
                                    updated: report.establishmentsUpdated,
                                    remaining: report.establishmentsRemaining,
                                })}
                            </Text>
                            <Text size='sm'>
                                {t('pages.cityDetail.guide.reportFoods', { created: report.foodsCreated })}
                            </Text>
                            <Text size='sm'>
                                {t('pages.cityDetail.guide.reportCity', { count: report.cityTextsFilled })}
                            </Text>
                            {report.notes.map((note) => (
                                <Text key={note} size='sm' c='dimmed'>
                                    {note}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                ) : null}
            </Paper>

            <SegmentedControl
                value={section}
                onChange={(value) => setSection(String(value))}
                data={[
                    { value: 'districts', label: t('pages.cityDetail.guide.districts') },
                    { value: 'food', label: t('pages.cityDetail.guide.food') },
                ]}
            />

            <Box hidden={section !== 'districts'}>
                <DistrictWorkspace
                    key={`districts-${cityId}-${token}`}
                    cityId={cityId}
                    lang={lang}
                    languages={languages}
                    onLang={setLang}
                    onDirty={setDistrictDirty}
                    onCatalogChange={() => setCatalogVersion((value) => value + 1)}
                />
            </Box>
            <Box hidden={section !== 'food'}>
                <FoodWorkspace
                    key={`food-${cityId}-${token}`}
                    cityId={cityId}
                    lang={lang}
                    languages={languages}
                    catalogVersion={catalogVersion}
                    onLang={setLang}
                    onDirty={setFoodDirty}
                />
            </Box>

            <Modal
                opened={confirm}
                onClose={() => !refreshing && setConfirm(false)}
                title={t('pages.cityDetail.guide.refreshConfirmTitle')}
            >
                <Stack gap='md'>
                    <Text size='sm'>{t('pages.cityDetail.guide.refreshConfirmBody')}</Text>
                    {dirty ? (
                        <Text size='sm'>{t('pages.cityDetail.guide.refreshUnsaved')}</Text>
                    ) : null}
                    <Checkbox
                        label={t('pages.cityDetail.guide.force')}
                        description={t('pages.cityDetail.guide.forceHint')}
                        checked={force}
                        onChange={(event) => setForce(event.currentTarget.checked)}
                    />
                    <Group justify='flex-end'>
                        <Button variant='default' onClick={() => setConfirm(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button loading={refreshing} onClick={refresh}>
                            {t('pages.cityDetail.guide.run')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

const DistrictWorkspace = ({
    cityId,
    lang,
    languages,
    onLang,
    onDirty,
    onCatalogChange,
}: {
    cityId: number;
    lang: Languages;
    languages: Languages[];
    onLang: (language: Languages) => void;
    onDirty: (dirty: boolean) => void;
    onCatalogChange: () => void;
}) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<CityDistrictRO[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [drafts, setDrafts] = useState<Record<string, TextDraft>>({});
    const [card, setCard] = useState<CardDraft | null>(null);
    const [baseline, setBaseline] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [addTitle, setAddTitle] = useState('');
    const [serviceOpen, setServiceOpen] = useState(false);
    const [pendingId, setPendingId] = useState<number | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const selected = rows.find((row) => row.id === selectedId) || null;
    const draft = drafts[lang];
    const dirty = !!selected && JSON.stringify({ drafts, card }) !== baseline;

    useEffect(() => {
        onDirty(dirty);
    }, [dirty, onDirty]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityDistrictsApi.query({
                    page: 1,
                    limit: 100,
                    sort: 'title',
                    order: 'ASC',
                    where: { cityId },
                });

                if (cancelled) return;

                const items = data?.items || [];

                setRows(items);
                setSelectedId((current) =>
                    items.some((row) => row.id === current) ? current : (items[0]?.id ?? null),
                );
            } catch (error) {
                if (!cancelled) notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [cityId]);

    useEffect(() => {
        if (!selected) {
            setDrafts({});
            setCard(null);
            setBaseline('');

            return;
        }

        const nextDrafts: Record<string, TextDraft> = {};

        for (const language of languages) {
            const locale = localeOf(selected.locales, language);

            nextDrafts[language] = {
                name: locale?.name || '',
                portrait: locale?.portrait || '',
                why: locale?.why || '',
                stay: locale?.stay || '',
                status: locale?.status || GuideLocaleStatusEnum.EMPTY,
            };
        }

        const nextCard: CardDraft = {
            title: selected.title,
            lat: selected.lat == null ? '' : String(selected.lat),
            lng: selected.lng == null ? '' : String(selected.lng),
            status: selected.status,
        };

        setDrafts(nextDrafts);
        setCard(nextCard);
        setBaseline(JSON.stringify({ drafts: nextDrafts, card: nextCard }));
    }, [selected, languages]);

    const visible = rows.filter((row) => {
        const needle = query.trim().toLowerCase();

        if (!needle) return true;

        const name = localeOf(row.locales, lang)?.name || row.title;

        return name.toLowerCase().includes(needle) || row.title.toLowerCase().includes(needle);
    });
    const readyCount = rows.filter(
        (row) => localeOf(row.locales, lang)?.status === GuideLocaleStatusEnum.READY,
    ).length;
    const chips: LanguageChip[] = languages.map((language) => {
        const item = drafts[language];
        const saved = selected ? localeOf(selected.locales, language) : undefined;
        const changed = JSON.stringify(item) !== JSON.stringify({
            name: saved?.name || '',
            portrait: saved?.portrait || '',
            why: saved?.why || '',
            stay: saved?.stay || '',
            status: saved?.status || GuideLocaleStatusEnum.EMPTY,
        });

        return {
            value: language,
            label: language.toUpperCase(),
            mark: changed
                ? 'dirty'
                : item?.status === GuideLocaleStatusEnum.READY
                  ? 'reviewed'
                  : item && textReady(item)
                    ? 'filled'
                    : 'empty',
        };
    });

    const choose = (id: number) => {
        if (id === selectedId) return;
        if (dirty) {
            setPendingId(id);

            return;
        }

        setSelectedId(id);
    };

    const save = async (): Promise<boolean> => {
        if (!selected || !card || !draft) return false;

        const lat = card.lat.trim() === '' ? null : Number(card.lat);
        const lng = card.lng.trim() === '' ? null : Number(card.lng);

        if ((lat == null) !== (lng == null) || (lat != null && Number.isNaN(lat)) || (lng != null && Number.isNaN(lng))) {
            notifications.show({ color: 'red', message: t('pages.cityDetail.guide.coordsHint') });

            return false;
        }

        const anyReady = languages.some((language) => {
            const item = drafts[language];

            return item?.status === GuideLocaleStatusEnum.READY && textReady(item);
        });

        if (card.status === GuideStatusEnum.PUBLISHED && !anyReady) {
            notifications.show({ color: 'red', message: t('pages.cityDetail.guide.publishNeedsText') });

            return false;
        }

        for (const language of languages) {
            const item = drafts[language];

            if (item?.status === GuideLocaleStatusEnum.READY && !textReady(item)) {
                notifications.show({ color: 'red', message: t('pages.cityDetail.guide.textReadyHint') });

                return false;
            }
        }

        setSaving(true);

        try {
            let latest = selected;

            for (const language of languages) {
                const item = drafts[language];
                const saved = localeOf(selected.locales, language);
                const same =
                    (item?.name || '') === (saved?.name || '') &&
                    (item?.portrait || '') === (saved?.portrait || '') &&
                    (item?.why || '') === (saved?.why || '') &&
                    (item?.stay || '') === (saved?.stay || '') &&
                    (item?.status || GuideLocaleStatusEnum.EMPTY) ===
                        (saved?.status || GuideLocaleStatusEnum.EMPTY);

                if (!item || same) continue;

                const { data } = await api.cityDistrictsApi.upsertLocale({
                    id: selected.id,
                    data: {
                        language,
                        name: item.name.trim(),
                        portrait: item.portrait.trim() || null,
                        why: item.why.trim() || null,
                        stay: item.stay.trim() || null,
                        status: item.status,
                    },
                });

                if (data) latest = data;
            }

            const cardSame =
                card.title.trim() === selected.title &&
                lat === (selected.lat ?? null) &&
                lng === (selected.lng ?? null) &&
                card.status === selected.status;

            if (!cardSame) {
                const { data } = await api.cityDistrictsApi.update({
                    id: selected.id,
                    data: {
                        title: card.title.trim(),
                        lat,
                        lng,
                        status: card.status,
                    },
                });

                if (data) latest = data;
            }

            setRows((current) => current.map((row) => (row.id === latest.id ? latest : row)));
            notifications.show({ color: 'green', message: t('pages.cityDetail.guide.saved') });

            return true;
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return false;
        } finally {
            setSaving(false);
        }
    };

    const create = async () => {
        const title = addTitle.trim();

        if (!title) return;

        setSaving(true);

        try {
            const { data } = await api.cityDistrictsApi.create({ data: { title, cityId } });

            if (data) {
                setRows((current) =>
                    [...current, data].sort((a, b) => a.title.localeCompare(b.title)),
                );
                setSelectedId(data.id);
                setAddTitle('');
                onCatalogChange();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!selected) return;

        setSaving(true);

        try {
            await api.cityDistrictsApi.remove(selected.id);
            const next = rows.filter((row) => row.id !== selected.id);

            setRows(next);
            setSelectedId(next[0]?.id ?? null);
            setDeleteOpen(false);
            onCatalogChange();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const patch = (field: keyof TextDraft, value: string | GuideLocaleStatusEnum) => {
        setDrafts((current) => ({
            ...current,
            [lang]: { ...current[lang], [field]: value },
        }));
    };

    return (
        <Stack gap='sm'>
            <Text size='sm' c='dimmed'>
                {t('pages.cityDetail.guide.readyCount', { ready: readyCount, total: rows.length })}
            </Text>
            <Grid gap='md'>
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <Stack gap='sm'>
                        <TextInput
                            placeholder={t('pages.cityDetail.guide.searchDistricts')}
                            value={query}
                            onChange={(event) => setQuery(event.currentTarget.value)}
                        />
                        <ScrollArea h={420} offsetScrollbars>
                            <Stack gap={4}>
                                {visible.map((row) => {
                                    const name = localeOf(row.locales, lang)?.name || row.title;
                                    const active = row.id === selectedId;

                                    return (
                                        <UnstyledButton key={row.id} onClick={() => choose(row.id)}>
                                            <Group
                                                justify='space-between'
                                                wrap='nowrap'
                                                px='sm'
                                                py={8}
                                                style={{
                                                    borderRadius: 8,
                                                    background: active
                                                        ? 'var(--mantine-color-blue-light)'
                                                        : undefined,
                                                }}
                                            >
                                                <Text size='sm' fw={active ? 600 : 400} lineClamp={1}>
                                                    {name}
                                                </Text>
                                                <Badge
                                                    size='xs'
                                                    variant='light'
                                                    color={
                                                        row.status === GuideStatusEnum.PUBLISHED
                                                            ? 'green'
                                                            : 'gray'
                                                    }
                                                >
                                                    {t(`enums.guideStatus.${row.status}`)}
                                                </Badge>
                                            </Group>
                                        </UnstyledButton>
                                    );
                                })}
                            </Stack>
                        </ScrollArea>
                        <Group align='flex-end' wrap='nowrap'>
                            <TextInput
                                placeholder={t('pages.cityDetail.guide.addPlaceholder')}
                                value={addTitle}
                                style={{ flex: 1 }}
                                onChange={(event) => setAddTitle(event.currentTarget.value)}
                                onKeyDown={(event) => {
                                    if (event.key !== 'Enter') return;

                                    event.preventDefault();
                                    if (dirty) setPendingId(-1);
                                    else create();
                                }}
                            />
                            <Button
                                variant='light'
                                leftSection={<IconPlus size={16} />}
                                loading={saving}
                                onClick={() => (dirty ? setPendingId(-1) : create())}
                            >
                                {t('pages.cityDetail.guide.add')}
                            </Button>
                        </Group>
                    </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 8 }}>
                    {!selected || !draft || !card ? (
                        <Text size='sm' c='dimmed'>
                            {loading ? t('common.loading') : t('pages.cityDetail.guide.emptyDistricts')}
                        </Text>
                    ) : (
                        <Stack gap='sm' maw={680}>
                            <LanguageChips
                                languages={chips}
                                value={lang}
                                onChange={(value) => onLang(value as Languages)}
                            />
                            {dirty ? (
                                <Text size='xs' c='orange'>
                                    {t('pages.cityDetail.guide.unsaved')}
                                </Text>
                            ) : null}
                            <TextInput
                                label={t('pages.cityDetail.guide.name')}
                                value={draft.name}
                                maxLength={180}
                                onChange={(event) => patch('name', event.currentTarget.value)}
                            />
                            <Textarea
                                label={t('pages.cityDetail.guide.portrait')}
                                description={t('pages.cityDetail.guide.portraitHint')}
                                minRows={4}
                                autosize
                                maxLength={2000}
                                value={draft.portrait}
                                onChange={(event) => patch('portrait', event.currentTarget.value)}
                            />
                            <Textarea
                                label={t('pages.cityDetail.guide.why')}
                                description={t('pages.cityDetail.guide.whyHint')}
                                minRows={3}
                                autosize
                                maxLength={2000}
                                value={draft.why}
                                onChange={(event) => patch('why', event.currentTarget.value)}
                            />
                            <Textarea
                                label={t('pages.cityDetail.guide.stay')}
                                description={t('pages.cityDetail.guide.stayHint')}
                                minRows={3}
                                autosize
                                maxLength={2000}
                                value={draft.stay}
                                onChange={(event) => patch('stay', event.currentTarget.value)}
                            />
                            <Switch
                                label={t('pages.cityDetail.guide.textReady')}
                                description={t('pages.cityDetail.guide.textReadyHint')}
                                checked={draft.status === GuideLocaleStatusEnum.READY}
                                disabled={!textReady(draft) && draft.status !== GuideLocaleStatusEnum.READY}
                                onChange={(event) =>
                                    patch(
                                        'status',
                                        event.currentTarget.checked
                                            ? GuideLocaleStatusEnum.READY
                                            : GuideLocaleStatusEnum.EMPTY,
                                    )
                                }
                            />
                            <Group justify='space-between'>
                                <Button loading={saving} onClick={save}>
                                    {t('common.save')}
                                </Button>
                                <Text size='xs' c='dimmed'>
                                    {t('pages.cityDetail.guide.saveHint')}
                                </Text>
                            </Group>
                            <UnstyledButton onClick={() => setServiceOpen((value) => !value)}>
                                <Text size='sm' fw={600}>
                                    {t('pages.cityDetail.guide.service')}
                                </Text>
                            </UnstyledButton>
                            <Collapse expanded={serviceOpen}>
                                <Stack gap='sm'>
                                    <Text size='xs' c='dimmed'>
                                        {t('pages.cityDetail.guide.serviceHint')}
                                    </Text>
                                    <TextInput
                                        label={t('pages.cityDetail.guide.canonical')}
                                        value={card.title}
                                        maxLength={180}
                                        onChange={(event) =>
                                            setCard({ ...card, title: event.currentTarget.value })
                                        }
                                    />
                                    <Group grow>
                                        <NumberInput
                                            label={t('pages.cityDetail.guide.latitude')}
                                            value={card.lat === '' ? '' : Number(card.lat)}
                                            decimalScale={5}
                                            onChange={(value) =>
                                                setCard({
                                                    ...card,
                                                    lat: value === '' || value == null ? '' : String(value),
                                                })
                                            }
                                        />
                                        <NumberInput
                                            label={t('pages.cityDetail.guide.longitude')}
                                            value={card.lng === '' ? '' : Number(card.lng)}
                                            decimalScale={5}
                                            onChange={(value) =>
                                                setCard({
                                                    ...card,
                                                    lng: value === '' || value == null ? '' : String(value),
                                                })
                                            }
                                        />
                                    </Group>
                                    <Switch
                                        label={t('pages.cityDetail.guide.publish')}
                                        checked={card.status === GuideStatusEnum.PUBLISHED}
                                        onChange={(event) =>
                                            setCard({
                                                ...card,
                                                status: event.currentTarget.checked
                                                    ? GuideStatusEnum.PUBLISHED
                                                    : GuideStatusEnum.DRAFT,
                                            })
                                        }
                                    />
                                    <Button
                                        color='red'
                                        variant='light'
                                        leftSection={<IconTrash size={16} />}
                                        onClick={() => setDeleteOpen(true)}
                                    >
                                        {t('pages.cityDetail.guide.remove')}
                                    </Button>
                                </Stack>
                            </Collapse>
                        </Stack>
                    )}
                </Grid.Col>
            </Grid>
            <Modal
                opened={pendingId != null}
                onClose={() => setPendingId(null)}
                title={t('pages.cityDetail.guide.unsavedTitle')}
            >
                <Group justify='flex-end'>
                    <Button variant='default' onClick={() => setPendingId(null)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant='light'
                        onClick={() => {
                            const next = pendingId;

                            setPendingId(null);
                            if (next === -1) {
                                create();

                                return;
                            }

                            if (next != null) setSelectedId(next);
                        }}
                    >
                        {t('pages.cityDetail.guide.discard')}
                    </Button>
                    <Button
                        loading={saving}
                        onClick={async () => {
                            const next = pendingId;
                            const ok = await save();

                            if (!ok) return;

                            setPendingId(null);
                            if (next === -1) create();
                            else if (next != null) setSelectedId(next);
                        }}
                    >
                        {t('common.save')}
                    </Button>
                </Group>
            </Modal>
            <Modal
                opened={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                title={t('pages.cityDetail.guide.remove')}
            >
                <Stack gap='md'>
                    <Text size='sm'>{t('pages.cityDetail.guide.removeConfirm')}</Text>
                    <Group justify='flex-end'>
                        <Button variant='default' onClick={() => setDeleteOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button color='red' loading={saving} onClick={remove}>
                            {t('common.delete')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

const FoodWorkspace = ({
    cityId,
    lang,
    languages,
    catalogVersion,
    onLang,
    onDirty,
}: {
    cityId: number;
    lang: Languages;
    languages: Languages[];
    catalogVersion: number;
    onLang: (language: Languages) => void;
    onDirty: (dirty: boolean) => void;
}) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<CityFoodRO[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [drafts, setDrafts] = useState<Record<string, FoodText>>({});
    const [card, setCard] = useState<FoodCard | null>(null);
    const [places, setPlaces] = useState<PlaceDraft[]>([]);
    const [baseline, setBaseline] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [addTitle, setAddTitle] = useState('');
    const [addPlace, setAddPlace] = useState<string | null>(null);
    const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
    const [pendingId, setPendingId] = useState<number | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [serviceOpen, setServiceOpen] = useState(false);
    const selected = rows.find((row) => row.id === selectedId) || null;
    const draft = drafts[lang];
    const dirty = !!selected && JSON.stringify({ drafts, card, places }) !== baseline;

    useEffect(() => {
        onDirty(dirty);
    }, [dirty, onDirty]);

    useEffect(() => {
        let cancelled = false;

        api.cityEstablishmentsApi
            .query({ page: 1, limit: 100, sort: 'title', order: 'ASC', where: { cityId } })
            .then(({ data }) => {
                if (cancelled) return;

                setOptions(
                    (data?.items || []).map((row) => ({ value: String(row.id), label: row.title })),
                );
            })
            .catch(() => {
                if (!cancelled) setOptions([]);
            });

        return () => {
            cancelled = true;
        };
    }, [cityId]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityFoodsApi.query({
                    page: 1,
                    limit: 100,
                    sort: 'title',
                    order: 'ASC',
                    where: { cityId },
                });

                if (cancelled) return;

                const items = data?.items || [];

                setRows(items);
                setSelectedId((current) =>
                    items.some((row) => row.id === current) ? current : (items[0]?.id ?? null),
                );
            } catch (error) {
                if (!cancelled) notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [cityId]);

    useEffect(() => {
        if (!selected) {
            setDrafts({});
            setCard(null);
            setPlaces([]);
            setBaseline('');

            return;
        }

        const nextDrafts: Record<string, FoodText> = {};

        for (const language of languages) {
            const locale = localeOf(selected.locales, language);

            nextDrafts[language] = {
                name: locale?.name || '',
                pitch: locale?.pitch || '',
                status: locale?.status || GuideLocaleStatusEnum.EMPTY,
            };
        }

        const nextCard: FoodCard = {
            title: selected.title,
            districtId: selected.districtId,
            status: selected.status,
        };
        const nextPlaces: PlaceDraft[] = (selected.places || []).map((place) => ({
            establishmentId: place.establishmentId,
            title: place.establishmentTitle || `#${place.establishmentId}`,
            orderNotes: { ...(place.orderNotes || {}) },
        }));

        setDrafts(nextDrafts);
        setCard(nextCard);
        setPlaces(nextPlaces);
        setBaseline(JSON.stringify({ drafts: nextDrafts, card: nextCard, places: nextPlaces }));
    }, [selected, languages]);

    const visible = rows.filter((row) => {
        const needle = query.trim().toLowerCase();

        if (!needle) return true;

        const name = localeOf(row.locales, lang)?.name || row.title;

        return name.toLowerCase().includes(needle) || row.title.toLowerCase().includes(needle);
    });
    const chips: LanguageChip[] = languages.map((language) => {
        const item = drafts[language];
        const saved = selected ? localeOf(selected.locales, language) : undefined;
        const changed =
            (item?.name || '') !== (saved?.name || '') ||
            (item?.pitch || '') !== (saved?.pitch || '') ||
            (item?.status || GuideLocaleStatusEnum.EMPTY) !==
                (saved?.status || GuideLocaleStatusEnum.EMPTY);

        return {
            value: language,
            label: language.toUpperCase(),
            mark: changed
                ? 'dirty'
                : item?.status === GuideLocaleStatusEnum.READY
                  ? 'reviewed'
                  : item?.name.trim() || item?.pitch.trim()
                    ? 'filled'
                    : 'empty',
        };
    });
    const pitchReady = !!draft?.name.trim() && !!draft?.pitch.trim();

    const save = async (): Promise<boolean> => {
        if (!selected || !card || !draft) return false;

        const anyReady = languages.some((language) => {
            const item = drafts[language];

            return (
                item?.status === GuideLocaleStatusEnum.READY &&
                !!item.name.trim() &&
                !!item.pitch.trim()
            );
        });

        if (card.status === GuideStatusEnum.PUBLISHED && !anyReady) {
            notifications.show({ color: 'red', message: t('pages.cityDetail.guide.publishNeedsText') });

            return false;
        }

        for (const language of languages) {
            const item = drafts[language];

            if (item?.status === GuideLocaleStatusEnum.READY && (!item.name.trim() || !item.pitch.trim())) {
                notifications.show({ color: 'red', message: t('pages.cityDetail.guide.textReadyHint') });

                return false;
            }
        }

        setSaving(true);

        try {
            let latest = selected;

            for (const language of languages) {
                const item = drafts[language];
                const saved = localeOf(selected.locales, language);
                const same =
                    (item?.name || '') === (saved?.name || '') &&
                    (item?.pitch || '') === (saved?.pitch || '') &&
                    (item?.status || GuideLocaleStatusEnum.EMPTY) ===
                        (saved?.status || GuideLocaleStatusEnum.EMPTY);

                if (!item || same) continue;

                const { data } = await api.cityFoodsApi.upsertLocale({
                    id: selected.id,
                    data: {
                        language,
                        name: item.name.trim(),
                        pitch: item.pitch.trim() || null,
                        status: item.status,
                    },
                });

                if (data) latest = data;
            }

            const { data } = await api.cityFoodsApi.update({
                id: selected.id,
                data: {
                    title: card.title.trim(),
                    districtId: card.districtId,
                    status: card.status,
                    places: places.map((place, index) => ({
                        establishmentId: place.establishmentId,
                        sort: index,
                        orderNotes: Object.fromEntries(
                            Object.entries(place.orderNotes)
                                .map(([key, value]) => [key, value.trim()])
                                .filter(([, value]) => value),
                        ),
                    })),
                },
            });

            if (data) latest = data;

            setRows((current) => current.map((row) => (row.id === latest.id ? latest : row)));
            notifications.show({ color: 'green', message: t('pages.cityDetail.guide.saved') });

            return true;
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return false;
        } finally {
            setSaving(false);
        }
    };

    const create = async () => {
        const title = addTitle.trim();

        if (!title) return;

        setSaving(true);

        try {
            const { data } = await api.cityFoodsApi.create({ data: { title, cityId } });

            if (data) {
                setRows((current) =>
                    [...current, data].sort((a, b) => a.title.localeCompare(b.title)),
                );
                setSelectedId(data.id);
                setAddTitle('');
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!selected) return;

        setSaving(true);

        try {
            await api.cityFoodsApi.remove(selected.id);
            const next = rows.filter((row) => row.id !== selected.id);

            setRows(next);
            setSelectedId(next[0]?.id ?? null);
            setDeleteOpen(false);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack gap='sm'>
            <Grid gap='md'>
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <Stack gap='sm'>
                        <TextInput
                            placeholder={t('pages.cityDetail.guide.searchFoods')}
                            value={query}
                            onChange={(event) => setQuery(event.currentTarget.value)}
                        />
                        <ScrollArea h={420} offsetScrollbars>
                            <Stack gap={4}>
                                {visible.map((row) => {
                                    const name = localeOf(row.locales, lang)?.name || row.title;
                                    const active = row.id === selectedId;

                                    return (
                                        <UnstyledButton
                                            key={row.id}
                                            onClick={() => {
                                                if (row.id === selectedId) return;
                                                if (dirty) {
                                                    setPendingId(row.id);

                                                    return;
                                                }

                                                setSelectedId(row.id);
                                            }}
                                        >
                                            <Group
                                                justify='space-between'
                                                wrap='nowrap'
                                                px='sm'
                                                py={8}
                                                style={{
                                                    borderRadius: 8,
                                                    background: active
                                                        ? 'var(--mantine-color-blue-light)'
                                                        : undefined,
                                                }}
                                            >
                                                <Text size='sm' fw={active ? 600 : 400} lineClamp={1}>
                                                    {name}
                                                </Text>
                                                <Badge
                                                    size='xs'
                                                    variant='light'
                                                    color={
                                                        row.status === GuideStatusEnum.PUBLISHED
                                                            ? 'green'
                                                            : 'gray'
                                                    }
                                                >
                                                    {t(`enums.guideStatus.${row.status}`)}
                                                </Badge>
                                            </Group>
                                        </UnstyledButton>
                                    );
                                })}
                            </Stack>
                        </ScrollArea>
                        <Group align='flex-end' wrap='nowrap'>
                            <TextInput
                                placeholder={t('pages.cityDetail.guide.addFoodPlaceholder')}
                                value={addTitle}
                                style={{ flex: 1 }}
                                onChange={(event) => setAddTitle(event.currentTarget.value)}
                                onKeyDown={(event) => {
                                    if (event.key !== 'Enter') return;

                                    event.preventDefault();
                                    if (dirty) setPendingId(-1);
                                    else create();
                                }}
                            />
                            <Button
                                variant='light'
                                leftSection={<IconPlus size={16} />}
                                loading={saving}
                                onClick={() => (dirty ? setPendingId(-1) : create())}
                            >
                                {t('pages.cityDetail.guide.add')}
                            </Button>
                        </Group>
                    </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 8 }}>
                    {!selected || !draft || !card ? (
                        <Text size='sm' c='dimmed'>
                            {loading ? t('common.loading') : t('pages.cityDetail.guide.emptyFoods')}
                        </Text>
                    ) : (
                        <Stack gap='sm' maw={680}>
                            <LanguageChips
                                languages={chips}
                                value={lang}
                                onChange={(value) => onLang(value as Languages)}
                            />
                            <TextInput
                                label={t('pages.cityDetail.guide.name')}
                                value={draft.name}
                                maxLength={180}
                                onChange={(event) =>
                                    setDrafts((current) => ({
                                        ...current,
                                        [lang]: { ...current[lang], name: event.currentTarget.value },
                                    }))
                                }
                            />
                            <Textarea
                                label={t('pages.cityDetail.guide.pitch')}
                                description={t('pages.cityDetail.guide.pitchHint')}
                                minRows={4}
                                autosize
                                maxLength={4000}
                                value={draft.pitch}
                                onChange={(event) =>
                                    setDrafts((current) => ({
                                        ...current,
                                        [lang]: { ...current[lang], pitch: event.currentTarget.value },
                                    }))
                                }
                            />
                            <Switch
                                label={t('pages.cityDetail.guide.textReady')}
                                description={t('pages.cityDetail.guide.textReadyHint')}
                                checked={draft.status === GuideLocaleStatusEnum.READY}
                                disabled={!pitchReady && draft.status !== GuideLocaleStatusEnum.READY}
                                onChange={(event) =>
                                    setDrafts((current) => ({
                                        ...current,
                                        [lang]: {
                                            ...current[lang],
                                            status: event.currentTarget.checked
                                                ? GuideLocaleStatusEnum.READY
                                                : GuideLocaleStatusEnum.EMPTY,
                                        },
                                    }))
                                }
                            />
                            <DistrictSelect
                                key={`${cityId}-${catalogVersion}`}
                                cityId={cityId}
                                value={card.districtId}
                                label={t('pages.cityDetail.guide.district')}
                                onChange={(districtId) => setCard({ ...card, districtId })}
                            />
                            <Stack gap='xs'>
                                <Text size='sm' fw={600}>
                                    {t('pages.cityDetail.guide.tables')}
                                </Text>
                                {places.length ? (
                                    places.map((place) => (
                                        <Paper key={place.establishmentId} withBorder p='sm' radius='md'>
                                            <Group justify='space-between' mb='xs'>
                                                <Text size='sm' fw={600}>
                                                    {place.title}
                                                </Text>
                                                <Button
                                                    size='compact-xs'
                                                    variant='subtle'
                                                    color='red'
                                                    onClick={() =>
                                                        setPlaces((current) =>
                                                            current.filter(
                                                                (item) =>
                                                                    item.establishmentId !==
                                                                    place.establishmentId,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    {t('common.delete')}
                                                </Button>
                                            </Group>
                                            <TextInput
                                                label={t('pages.cityDetail.guide.order')}
                                                maxLength={300}
                                                value={place.orderNotes[lang] || ''}
                                                onChange={(event) =>
                                                    setPlaces((current) =>
                                                        current.map((item) =>
                                                            item.establishmentId === place.establishmentId
                                                                ? {
                                                                      ...item,
                                                                      orderNotes: {
                                                                          ...item.orderNotes,
                                                                          [lang]: event.currentTarget.value,
                                                                      },
                                                                  }
                                                                : item,
                                                        ),
                                                    )
                                                }
                                            />
                                        </Paper>
                                    ))
                                ) : (
                                    <Text size='sm' c='dimmed'>
                                        {t('pages.cityDetail.guide.noTables')}
                                    </Text>
                                )}
                                <Group align='flex-end'>
                                    <Select
                                        label={t('pages.cityDetail.guide.pickTable')}
                                        data={options.filter(
                                            (option) =>
                                                !places.some(
                                                    (place) => String(place.establishmentId) === option.value,
                                                ),
                                        )}
                                        value={addPlace}
                                        searchable
                                        clearable
                                        style={{ flex: 1 }}
                                        onChange={(value) => setAddPlace(value == null ? null : String(value))}
                                    />
                                    <Button
                                        variant='light'
                                        onClick={() => {
                                            if (!addPlace) return;

                                            const option = options.find((item) => item.value === addPlace);

                                            setPlaces((current) => [
                                                ...current,
                                                {
                                                    establishmentId: Number(addPlace),
                                                    title: option?.label || addPlace,
                                                    orderNotes: {},
                                                },
                                            ]);
                                            setAddPlace(null);
                                        }}
                                    >
                                        {t('pages.cityDetail.guide.addTable')}
                                    </Button>
                                </Group>
                            </Stack>
                            <Group justify='space-between'>
                                <Button loading={saving} onClick={save}>
                                    {t('common.save')}
                                </Button>
                                <Text size='xs' c='dimmed'>
                                    {t('pages.cityDetail.guide.saveHint')}
                                </Text>
                            </Group>
                            <UnstyledButton onClick={() => setServiceOpen((value) => !value)}>
                                <Text size='sm' fw={600}>
                                    {t('pages.cityDetail.guide.service')}
                                </Text>
                            </UnstyledButton>
                            <Collapse expanded={serviceOpen}>
                                <Stack gap='sm'>
                                    <TextInput
                                        label={t('pages.cityDetail.guide.canonical')}
                                        value={card.title}
                                        maxLength={180}
                                        onChange={(event) =>
                                            setCard({ ...card, title: event.currentTarget.value })
                                        }
                                    />
                                    <Switch
                                        label={t('pages.cityDetail.guide.publish')}
                                        checked={card.status === GuideStatusEnum.PUBLISHED}
                                        onChange={(event) =>
                                            setCard({
                                                ...card,
                                                status: event.currentTarget.checked
                                                    ? GuideStatusEnum.PUBLISHED
                                                    : GuideStatusEnum.DRAFT,
                                            })
                                        }
                                    />
                                    <Button
                                        color='red'
                                        variant='light'
                                        leftSection={<IconTrash size={16} />}
                                        onClick={() => setDeleteOpen(true)}
                                    >
                                        {t('pages.cityDetail.guide.removeFood')}
                                    </Button>
                                </Stack>
                            </Collapse>
                        </Stack>
                    )}
                </Grid.Col>
            </Grid>
            <Modal
                opened={pendingId != null}
                onClose={() => setPendingId(null)}
                title={t('pages.cityDetail.guide.unsavedTitle')}
            >
                <Group justify='flex-end'>
                    <Button variant='default' onClick={() => setPendingId(null)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant='light'
                        onClick={() => {
                            const next = pendingId;

                            setPendingId(null);
                            if (next === -1) {
                                create();

                                return;
                            }

                            if (next != null && next > 0) setSelectedId(next);
                        }}
                    >
                        {t('pages.cityDetail.guide.discard')}
                    </Button>
                    <Button
                        loading={saving}
                        onClick={async () => {
                            const next = pendingId;
                            const ok = await save();

                            if (!ok || next == null) return;

                            setPendingId(null);
                            if (next === -1) create();
                            else setSelectedId(next);
                        }}
                    >
                        {t('common.save')}
                    </Button>
                </Group>
            </Modal>
            <Modal
                opened={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                title={t('pages.cityDetail.guide.removeFood')}
            >
                <Stack gap='md'>
                    <Text size='sm'>{t('pages.cityDetail.guide.removeFoodConfirm')}</Text>
                    <Group justify='flex-end'>
                        <Button variant='default' onClick={() => setDeleteOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button color='red' loading={saving} onClick={remove}>
                            {t('common.delete')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};
